import { Upload, X } from 'lucide-react';
import { useId, useState } from 'react';
import { SIGNATURE_ACCEPTED_TYPES, SIGNATURE_MAX_BYTES } from '#shared/types';
import { ImageReadError, readImageDataUrl } from '#client/lib/read-image-file';
import { Button } from './Button';

interface SignatureFieldProps {
  label: string;
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  helpText?: string | undefined;
  error?: string | undefined;
}

/**
 * A scanned signature, read into a data URL in the browser.
 *
 * Shares `readImageDataUrl` with the print builder's logo control, which is the
 * only other place the app takes an image. The two differ in their ceiling and
 * their accepted types — an animated GIF signature is not a thing — so those are
 * arguments rather than something baked into the helper.
 *
 * The preview is on a light background whatever the app's theme, because a
 * signature is almost always black on transparent and would be invisible on a
 * dark card. That is the same reason the print preview renders in a `.light`
 * scope.
 */
export function SignatureField({ label, value, onChange, helpText, error }: SignatureFieldProps) {
  const inputId = useId();
  const [readError, setReadError] = useState<string>();

  const message = error ?? readError;

  async function handleFile(file: File | undefined) {
    setReadError(undefined);
    if (!file) return;

    try {
      onChange(
        await readImageDataUrl(file, {
          maxBytes: SIGNATURE_MAX_BYTES,
          accepted: SIGNATURE_ACCEPTED_TYPES,
        }),
      );
    } catch (cause) {
      setReadError(cause instanceof ImageReadError ? cause.message : 'Could not read that file.');
    }
  }

  return (
    <div className="c-signature-field">
      <span className="c-field__label">{label}</span>

      <div className="c-signature-field__preview">
        {value ? (
          <img src={value} alt={`${label} preview`} />
        ) : (
          <span className="c-signature-field__empty">No signature on file</span>
        )}
      </div>

      <div className="c-signature-field__actions">
        <input
          id={inputId}
          className="sr-only"
          type="file"
          accept={SIGNATURE_ACCEPTED_TYPES.join(',')}
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
        {/*
          The native input is visually hidden and driven from this button, so the
          app owns the styling while the input keeps its keyboard and
          screen-reader behaviour.
        */}
        <Button
          size="sm"
          icon={<Upload />}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {value ? 'Replace' : 'Upload'}
        </Button>

        {value && (
          <Button size="sm" variant="ghost" icon={<X />} onClick={() => onChange(null)}>
            Remove
          </Button>
        )}
      </div>

      {helpText && !message && <p className="c-field__help">{helpText}</p>}
      {message && (
        <p className="c-field__error" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}
